import { useEffect, useState } from "react";
import { api, unwrap } from "@/lib/api";
import { Layers, Plus, X, ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PACKAGES } from "@/constants/testIds";

export function PackagesManager({ branches = [] }) {
  const [open, setOpen] = useState(true);
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);

  const refresh = () => {
    api.get("/packages").then((r) => setItems(unwrap(r) || [])).catch(() => {});
  };

  useEffect(() => {
    if (open) {
      refresh();
      api.get("/services").then((r) => setServices(unwrap(r) || [])).catch(() => {});
    }
  }, [open]);

  const deactivate = async (id) => {
    try {
      await api.delete(`/packages/${id}`);
      toast.success("Package deactivated");
      refresh();
    } catch {
      toast.error("Could not deactivate package");
    }
  };

  return (
    <div className="glass-primary rounded-[20px] overflow-hidden" data-testid={PACKAGES.root}>
      <button
        onClick={() => setOpen((x) => !x)}
        data-testid={PACKAGES.toggle}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/30 transition"
      >
        <div className="flex items-center gap-3">
          <Layers size={16} className="text-[var(--color-gold-deeper)]" />
          <div>
            <div className="font-sora font-semibold text-[15px] text-[var(--color-text-primary)]">Packages</div>
            <div className="font-inter text-[12px] text-[var(--color-text-muted)]">
              Bundled services with savings — bridal, seasonal, custom
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {items.length > 0 && <span className="font-mono text-[12px] text-[var(--color-text-muted)]">{items.filter(p => p.is_active).length} active</span>}
          {open ? <ChevronUp size={16} className="text-[var(--color-text-muted)]" /> : <ChevronDown size={16} className="text-[var(--color-text-muted)]" />}
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 anim-slide-up space-y-3">
          <div className="flex justify-end">
            <button data-testid={PACKAGES.createBtn} onClick={() => setCreateOpen(true)} className="btn-3d btn-gold-3d btn-sm">
              <Plus size={14} /> Create package
            </button>
          </div>

          {items.length === 0 ? (
            <div className="font-inter text-[13px] text-[var(--color-text-muted)] text-center py-6">
              No packages yet. Create one to get started.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {items.map((p) => (
                <div key={p.id} data-testid={PACKAGES.row(p.id)} className="rounded-2xl p-4 border" style={{ background: "rgba(212,168,83,0.06)", borderColor: "rgba(212,168,83,0.30)" }}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <h3 className="font-sora text-[17px] font-semibold text-[var(--color-text-primary)]">{p.name}</h3>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="status-pill" style={{ background: "rgba(94,64,96,0.10)", color: "#5E4060", padding: "2px 8px", fontSize: 10 }}>{p.category}</span>
                        <span className="status-pill" style={{ background: "rgba(120,113,108,0.08)", color: "#78716C", padding: "2px 8px", fontSize: 10 }}>{p.valid_for_days}d valid</span>
                      </div>
                    </div>
                    {p.is_active && (
                      <button onClick={() => deactivate(p.id)} data-testid={PACKAGES.deactivateBtn(p.id)} className="w-7 h-7 rounded-full glass-subtle flex items-center justify-center hover:bg-white/80" aria-label="Deactivate">
                        <Trash2 size={12} className="text-[var(--color-danger-deep)]" />
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {(p.services || []).map((s, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-white/70 border border-white/80 text-[11px] font-inter text-[var(--color-text-secondary)]">{s.service_name}</span>
                    ))}
                  </div>
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="font-mono text-[11.5px] text-[var(--color-text-muted)] line-through">₹{p.individual_total}</div>
                      <div className="font-mono text-[26px] leading-none text-[var(--color-gold-deeper)] font-semibold">₹{p.package_price}</div>
                    </div>
                    <span className="status-pill" style={{ background: "rgba(22,163,74,0.10)", color: "#15803D", padding: "3px 10px", fontSize: 11.5 }}>
                      Save ₹{p.savings}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {createOpen && (
        <CreatePackageModal
          services={services}
          branches={branches}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); refresh(); }}
        />
      )}
    </div>
  );
}

function CreatePackageModal({ services, branches, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", description: "", category: "regular",
    services: [], package_price: "", valid_for_days: 90,
    branch_id: "",
  });

  const individualTotal = form.services.reduce((s, x) => s + (x.base_price || 0), 0);
  const price = Number(form.package_price) || 0;
  const savings = Math.max(0, individualTotal - price);
  const savingsPct = individualTotal > 0 ? Math.round((savings / individualTotal) * 100) : 0;
  const priceInvalid = price > 0 && price >= individualTotal;

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const addService = (s) => {
    if (form.services.some((x) => x.service_id === s.id)) return;
    setForm({ ...form, services: [...form.services, { service_id: s.id, service_name: s.name, base_price: s.base_price }] });
  };

  const removeService = (sid) => {
    setForm({ ...form, services: form.services.filter((x) => x.service_id !== sid) });
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (form.services.length === 0) { toast.error("Pick at least one service"); return; }
    if (priceInvalid || !price) { toast.error("Package price must be less than individual total"); return; }
    try {
      await api.post("/packages", {
        name: form.name.trim(),
        description: form.description || null,
        services: form.services,
        package_price: price,
        valid_for_days: Number(form.valid_for_days) || 90,
        category: form.category,
        is_active: true,
        branch_id: form.branch_id || null,
      });
      toast.success(`Package "${form.name}" created`);
      onCreated?.();
    } catch (e) {
      const data = e?.response?.data?.error;
      toast.error(data?.message || "Could not create package");
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center anim-fade-in"
         style={{ background: "rgba(28,25,23,0.40)", backdropFilter: "blur(12px)" }}
         data-testid={PACKAGES.createModal}>
      <div className="glass-elevated rounded-[24px] p-6 w-[min(640px,calc(100vw-32px))] max-h-[88vh] overflow-auto anim-slide-up-lg space-y-3">
        <div className="flex items-start justify-between mb-1">
          <div>
            <div className="overline mb-1">New package</div>
            <h2 className="font-display text-[22px] font-bold tracking-[-0.02em]">Bundle services with savings</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center"><X size={16} /></button>
        </div>

        <Row label="Package name">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Bridal Glow Package" className={inputCls} data-testid={PACKAGES.nameInput} />
        </Row>
        <Row label="Description (optional)">
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} data-testid={PACKAGES.descInput} />
        </Row>

        <div>
          <div className="overline mb-1.5">Category</div>
          <div className="flex flex-wrap gap-1.5">
            {["bridal", "seasonal", "regular", "custom"].map((c) => (
              <button key={c} type="button" onClick={() => setForm({ ...form, category: c })}
                data-testid={PACKAGES.categoryToggle(c)}
                className={`px-3 py-1.5 rounded-full text-[12.5px] font-inter font-medium border transition ${form.category === c ? "bg-[var(--color-mauve)]/12 border-[var(--color-mauve)]/40 text-[var(--color-mauve-deep)]" : "bg-white/70 border-white/80 text-[var(--color-text-secondary)]"}`}
              >
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="overline mb-1.5">Services</div>
          <div className="flex flex-wrap gap-1 mb-2">
            {form.services.map((s) => (
              <span key={s.service_id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[var(--color-gold-mid)]/12 border border-[var(--color-gold-mid)]/30 text-[11.5px] text-[var(--color-gold-deeper)]">
                {s.service_name} · ₹{s.base_price}
                <button onClick={() => removeService(s.service_id)} className="text-[var(--color-gold-deeper)] hover:text-[var(--color-text-primary)]">×</button>
              </span>
            ))}
          </div>
          <select onChange={(e) => {
            const s = services.find((x) => x.id === e.target.value);
            if (s) addService(s);
            e.target.value = "";
          }} className={inputCls} data-testid={PACKAGES.serviceSelect}>
            <option value="">+ Add a service…</option>
            {services.filter(s => !form.services.some(x => x.service_id === s.id)).map((s) => (
              <option key={s.id} value={s.id}>{s.name} · ₹{s.base_price}</option>
            ))}
          </select>
          <div className="mt-2 font-mono text-[12.5px] text-[var(--color-text-muted)]">
            Individual total: <span className="text-[var(--color-text-primary)] font-semibold">₹{individualTotal}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Row label="Package price ₹">
            <input
              type="number" step="50" min="0"
              value={form.package_price}
              onChange={(e) => setForm({ ...form, package_price: e.target.value })}
              className={`${inputCls} font-mono`}
              data-testid={PACKAGES.priceInput}
            />
          </Row>
          <Row label="Valid for (days)">
            <input
              type="number" step="1" min="1"
              value={form.valid_for_days}
              onChange={(e) => setForm({ ...form, valid_for_days: e.target.value })}
              className={inputCls}
              data-testid={PACKAGES.validDaysInput}
            />
          </Row>
        </div>

        {price > 0 && (
          priceInvalid
            ? <div className="font-inter text-[12.5px] text-[var(--color-danger-deep)]">Package price must be less than individual total (₹{individualTotal})</div>
            : <div className="font-inter text-[12.5px] text-[var(--color-success-deep)]">Save ₹{savings} ({savingsPct}% off)</div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button className="btn-3d btn-glass-3d btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-3d btn-gold-3d" onClick={submit} data-testid={PACKAGES.submitBtn}>
            <Plus size={14} /> Create package
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[14px] text-[var(--color-text-primary)] outline-none focus:border-[var(--color-gold-mid)] focus:ring-2 focus:ring-[var(--color-gold-mid)]/20 transition";

function Row({ label, children }) {
  return (
    <label className="block">
      <div className="font-inter text-[12.5px] font-medium text-[var(--color-text-secondary)] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
